import {
  DataGridProps,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import {FormLabel} from "./Form";
import CrudTable, {TableLabel} from "./Table";
import {BaseEntity} from "../../core/model/BaseEntity";
import {BaseEntityForm} from "../../core/model/BaseForm";
import {ConfirmDialogLabel} from "../confirm-dialog";
import {
  UseAddEntity,
  UseDeleteEntity,
  UseDeleteEntityById,
  UseFindEntity,
  UseFindEntityById,
  UseUpdateEntity,
} from "../../core/api/crud";
import {SvgIconOwnProps} from "@mui/material";
import {ReactNode, useEffect, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {translate} from "@util";
import {entity} from "@core/model";

type Props<T extends BaseEntity> = {
  useFindEntity: UseFindEntity<T>;
  useFindEntityById: UseFindEntityById<T>;
  useAddEntity?: UseAddEntity<T>;
  useUpdateEntity?: UseUpdateEntity<T>;
  useDeleteEntity?: UseDeleteEntity;
  useDeleteEntityById?: UseDeleteEntityById;
  entity: entity;
  topLeft?: ReactNode;
  readonly?: boolean;
  hideSearch?: boolean;
  form: BaseEntityForm;
  EntityIcon?: React.ComponentType<SvgIconOwnProps>;
  newEntityData?: Partial<T>;
  datagridProps?: Partial<DataGridProps>;
  formLabel?: Partial<FormLabel>;
  tableLabel?: Partial<TableLabel>;
  deleteDialogLabel?: Partial<ConfirmDialogLabel>;
  searchParams?: URLSearchParams;
  selectValues?: {[key: string]: {id: string | number; name: string}[]};
  CustomFormButton?: ReactNode;
  customActions?: {
    renderer: (
      params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
    ) => ReactNode | ReactNode[];
    length: number;
  };
  onEntityEdit?: (e: T) => void;
  onSearch: (text: string) => void;
  onClear: () => void;
  onRowClick?: (entity: T) => void;
  onPaginationSort?: (
    pagination: GridPaginationModel,
    sort: GridSortModel,
  ) => void;
  onFieldChange?: (field: string, value: any) => void;
};

export default function Crud<T extends BaseEntity>(props: Props<T>) {
  const {
    useFindEntity,
    useFindEntityById,
    useAddEntity,
    useUpdateEntity,
    useDeleteEntity,
    useDeleteEntityById,
    entity,
    hideSearch,
    topLeft,
    EntityIcon,
    newEntityData,
    form,
    datagridProps,
    formLabel,
    tableLabel,
    deleteDialogLabel,
    selectValues,
    searchParams,
    CustomFormButton,
    customActions,
    readonly,
    onEntityEdit,
    onSearch,
    onClear,
    onFieldChange,
    onRowClick,
    onPaginationSort,
  } = props;

  const [editId, setEditId] = useState<string | number>();

  const {
    data,
    isLoading: isDataGridLoading,
    isFetching: isDataGridFetching,
    refetch,
  } = useFindEntity(searchParams?.toString());

  const {
    data: entityData,
    isFetching: isFindByIdFetching,
    isError: isFindByIdError,
  } = useFindEntityById(editId ?? skipToken);

  const defResult = {isLoading: false, error: false};
  const addHook = useAddEntity?.() ?? [undefined, defResult];
  const [newEntity, addResult] = addHook;
  const {isLoading: isAddLoading = false, error: isAddError} = addResult;

  const updateHook = useUpdateEntity?.() ?? [undefined, defResult];
  const [updateEntity, updateResult] = updateHook;
  const {isLoading: isUpdateLoading = false, error: isUpdateError} =
    updateResult;

  const deleteHook = useDeleteEntity?.() ?? [undefined, defResult];
  const [deleteEntity, deleteResult] = deleteHook;
  const {isLoading: isDeleteLoading = false, error: isDeleteError} =
    deleteResult;

  const deleteByIdHook = useDeleteEntityById?.() ?? [undefined, defResult];
  const [deleteEntityById, deleteByIdResult] = deleteByIdHook;
  const {isLoading: isDeleteByIdLoading = false, error: isDeleteByIdError} =
    deleteByIdResult;

  const [editEntityData, setEditEntity] = useState<T>();

  useEffect(() => {
    setEditEntity(entityData);
  }, [entityData]);

  const handleDelete = (entities: T[]) => {
    if (entities.length === 1) {
      if (!deleteEntityById) {
        throw new Error("Delete by ID not supported");
      }
      deleteEntityById(entities[0].id!);
    } else {
      if (!deleteEntity) {
        throw new Error("Batch delete not supported");
      }
      deleteEntity(entities.map(e => e.id!));
    }
  };

  const handleSave = (entity: T) => {
    const col = form.columns.find(c => c.fieldType === "id");
    const fieldId = col?.field;
    if (!fieldId) return;

    if (entity[fieldId]) {
      entity.id = entity[fieldId];
      if (!updateEntity) {
        throw new Error("Update not supported");
      }
      updateEntity(entity)
        .unwrap()
        .then(() => setEditId(undefined));
    } else {
      if (!newEntity) {
        throw new Error("Add not supported");
      }
      newEntity(entity)
        .unwrap()
        .then(() => setEditId(undefined));
    }
  };

  const handleEdit = (id: string | undefined) => {
    setEditId(id);
    if (id === entityData?.id) {
      setEditEntity(entityData);
    }
    if (!id) {
      setEditEntity(undefined);
    }
  };

  useEffect(() => {
    if (onEntityEdit && editEntityData) {
      onEntityEdit(editEntityData);
    }
  }, [editEntityData]);

  return (
    <>
      <CrudTable<T>
        entity={entity}
        topLeft={topLeft}
        EntityIcon={EntityIcon}
        readOnly={readonly}
        onSave={handleSave}
        onDelete={handleDelete}
        onRowClick={onRowClick}
        onEdit={handleEdit}
        onPaginationSortChange={onPaginationSort}
        onRefresh={refetch}
        onSearch={onSearch}
        onClear={onClear}
        data={data}
        customActions={customActions}
        isDataGridLoading={
          (isDataGridLoading ||
            isDataGridFetching ||
            isDeleteLoading ||
            isDeleteByIdLoading) &&
          !(isAddError || isUpdateError || isDeleteError || isDeleteByIdError)
        }
        isFormLoading={
          (isAddLoading || isUpdateLoading) &&
          !(
            isAddError ||
            isUpdateError ||
            isDeleteError ||
            isDeleteByIdError ||
            isFindByIdError
          )
        }
        isFormFetching={isFindByIdFetching}
        isDeleteLoading={isDeleteLoading || isDeleteByIdLoading}
        form={form}
        newEntityData={newEntityData}
        editEntityData={editEntityData}
        datagridProps={datagridProps}
        formLabel={{
          ...{
            new: translate("common.action.new"),
            edit: translate("common.action.edit"),
            reset: translate("common.action.reset"),
            save: translate("common.action.save"),
          },
          ...formLabel,
        }}
        deleteDialogLabel={deleteDialogLabel}
        tableLabel={tableLabel}
        selectValues={selectValues}
        CustomFormButton={CustomFormButton}
        onFieldChange={onFieldChange}
        hideSearch={hideSearch}
      />
    </>
  );
}
